import { Col, Form, Row } from 'antd';
import { useEffect, useState, useMemo } from 'react';
import AnalysisChart from './chart';
import { useSelected } from '../../../../../providers/selectedContext';
import { updateSample } from '../../../../../api/data';
import { useAppContext } from '../../../../../providers/AppContext';
import { Accordion } from '../../../../../design-system/accordion';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { ColumnConfig, DataRow } from '../../../../../@types/data/types';
import DynamicDataTable from '../../../../forms/dynamicForm';
import FormWrapper from '../components/FormWrapper/FormWrapper';
import ConfirmClearModal from '../components/ConfirmClearModal/ConfirmClearModal';

const gasOilColumns: ColumnConfig[] = [
  { key: 'sg', header: 'Sg fraction' },
  { key: 'krg', header: 'Krg Lab' },
  { key: 'kro', header: 'Kro Lab' },
];

const AnalysisGasOil = () => {
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { configStates, updateConfigStates, data, updateData } = useAppContext();
  const [tableData, setTableData] = useState<DataRow[]>([]);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);

  useEffect(() => {
    const inputCurves = selectedInfo?.sample?.input_sample_curves?.gas_oil;
    if (inputCurves && inputCurves.sg_krel) {
      const loadedData = inputCurves.sg_krel.map((_, index) => ({
        sg: inputCurves?.sg_krel?.[index]?.toString() || '',
        krg: inputCurves?.krg_lab?.[index]?.toString() || '',
        kro: inputCurves?.kro_lab?.[index]?.toString() || '',
      }));
      setTableData([...loadedData, { sg: '', krg: '', kro: '' }]);
    } else {
      setTableData([{ sg: '', krg: '', kro: '' }]);
    }
  }, [selectedInfo]);

  const chartSeries = useMemo(() => {
    const validData = tableData.filter(row => row.sg && (row.krg || row.kro));

    const krgPoints = validData
      .map(row => [Number(row.sg), Number(row.krg)] as const)
      .filter(([sg, krg]) => !isNaN(sg) && !isNaN(krg));

    const kroPoints = validData
      .map(row => [Number(row.sg), Number(row.kro)] as const)
      .filter(([sg, kro]) => !isNaN(sg) && !isNaN(kro));

    return [
      {
        type: 'scatter',
        name: 'Krg Lab',
        data: krgPoints,
        color: '#fa3c3c',
        lineWidth: 2
      },
      {
        type: 'scatter',
        name: 'Kro Lab',
        data: kroPoints,
        color: '#059e05',
        lineWidth: 2
      }
    ] as Highcharts.SeriesOptionsType[];;
  }, [tableData]);

  const handleAddRow = () => {
    const emptyRow = gasOilColumns.reduce((acc, col) => ({ ...acc, [col.key]: '' }), {});
    setTableData([...tableData, emptyRow]);
  };

  const handleRemoveRow = () => {
    if (tableData.length > 1) {
      setTableData(tableData.slice(0, -1));
    }
  };

  const handleClearAll = () => {
    setIsClearModalOpen(true);
  };

  const handleCancelClear = () => {
    setIsClearModalOpen(false);
  };

  const handleConfirmClear = () => {
    setTableData([{ sg: '', krg: '', kro: '' }]);
    setIsClearModalOpen(false);
  };

  const hasDataToClear =
    tableData.length > 1 ||
    (tableData.length === 1 && Object.values(tableData[0] || {}).some(cell => cell !== ''));

  const handleSubmit = async () => {
    if (!selectedInfo?.sample) return;

    updateConfigStates({ loadingKrAnalysis: true });

    const dataToSubmit = tableData.filter(row =>
      Object.values(row).some(cell => cell !== '')
    );

    const payload = {
      input_sample_curves: {
        gas_oil: {
          sg_krel: dataToSubmit.map(row => Number(row.sg) || 0),
          krg_lab: dataToSubmit.map(row => Number(row.krg) || 0),
          kro_lab: dataToSubmit.map(row => Number(row.kro) || 0),
          fg_lab: null,
        },
        water_oil: null,
        pc_model: null,
      },
      sample_uid: selectedInfo.sample.uid,
    };

    try {
      const response = await updateSample(payload);
      const newWellSamplesData = data.wellSamples.map((sample) => {
        if (sample.uid === response.uid) {
          return response
        }

        return sample
      })
      updateData({ wellSamples: newWellSamplesData })
      setSelectedInfo({ sample: response });
    } catch (error) {
      console.error('error', error);
    }

    updateConfigStates({ loadingKrAnalysis: false });
  };

  return (
    <>
      <Accordion defaultOpen="lab-data-gas-oil">
        <Accordion.Item
          id="lab-data-gas-oil"
          header={
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
              <span>Laboratory Data</span>
              <span style={{ color: colors.gasOil }}>Gas-Oil</span>
            </div>
          }
        >
          <div style={{ padding: '0.5rem' }}>
            <Row>
              <Col span={12}>
                <Form onFinish={handleSubmit}>
                  <FormWrapper
                    onAddRow={handleAddRow}
                    onRemoveRow={handleRemoveRow}
                    onClearAll={handleClearAll}
                    showClearButton={hasDataToClear}
                    isSubmitting={configStates.loadingKrAnalysis}
                  >
                    <DynamicDataTable
                      columns={gasOilColumns}
                      data={tableData}
                      onDataChange={setTableData}
                    />
                  </FormWrapper>
                </Form>
              </Col>
              <Col span={12}>
                <AnalysisChart data={chartSeries} />
              </Col>
            </Row>
          </div>
        </Accordion.Item>
      </Accordion>
      <ConfirmClearModal
        isOpen={isClearModalOpen}
        onClose={handleCancelClear}
        onConfirm={handleConfirmClear}
      />
    </>
  );
};

export default AnalysisGasOil;
